import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Form, FormikHelpers, FormikValues } from 'formik';
import * as yup from 'yup';
import queryString from 'query-string';
import moment from 'moment';

import { FormAlert } from 'components/CASAForm';
import { FormContext, FormContextProvider, IFormContextType } from 'contexts/FormContext';
import { FormStep, IFormAlertType } from 'types/forms/formTypes';
import { AuBreadcrumbs, IAuBreadcrumbItem } from 'components/DTA';
import { processUrlParameters } from 'utilities';
import { DRONE_CONSTANTS, FORM_ERROR_MESSAGES } from 'internalConstants';

import { OrganisationContext } from 'contexts/OrganisationContext';
import { DroneEditStepProps, DroneData, DroneRegistrationFormData, FinalStepProps } from 'types/forms/droneRegistrationTypes';
import { useDroneService } from 'services';
import { DroneContext } from 'contexts';
import { ExitModal } from 'components/CASAForm/ExitModal';
import { Loader } from 'components/common';
import { featureSelector } from 'PortalStore/Selectors/EnvDataSelectors';
import { DroneDetailsStep, DroneRenewalSummaryStep } from 'forms/Drones/DroneRenewal';
import { determineFee, DroneEditStep, droneValidationSchema, FinalStep } from 'forms/Drones/Common';
import { getParamValue } from 'components/Portal/Utilities';

const DroneRenewal: React.FC = () => {
  const [isBackButtonClicked, setBackbuttonPress] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const B2CSettings = useSelector((state: any) => state.B2CSettings);
  const daysRPAexpiring = useSelector(state => featureSelector(state, 'Portal - RPA registration expiry warning period'));
  const daysRPAexpired = useSelector(state => featureSelector(state, 'Portal - RPA registration expired warning period'));
  const droneRegId = getParamValue('droneRegId') ?? '';

  const {
    referenceData,
    rateCards,
    isOrganisationRegistration,
    checkIfOtherManufacturer,
    checkIfOtherModel,
    checkIfHomeBuilt,
    dashboardUrl,
    manageDronesUrl,
    individualDrones,
    organisationDrones,
    setContextAlert,
    setSubmissionInProgress,
    featureDroneSerialNumberValidationIsActive,
    checkSerialNumberPrefix
  } = useContext(DroneContext);
  const { organisationDetailsResponse } = useContext(OrganisationContext);
  const droneService = useDroneService();

  useEffect(() => {
    document.title = 'Renew drone registration';
    // If westpac returns non succesful payment set an error alert
    // into the context for display on the drone list step
    const returnParameters = queryString.parse(window.location.search);
    if (returnParameters.summaryCode !== '0' && returnParameters.customRef) {
      setContextAlert({
        as: 'error',
        title: 'There was a problem with your card',
        message: 'Please check your card details or use a different card. Your application has been saved and can be submitted again from your profile screen.'
      });
    }
    window.history.pushState(window.location.search, '');
    window.addEventListener('popstate', onBackButtonEvent);
  }, []);

  const onBackButtonEvent = (e) => {
    e.preventDefault();
    if (!isBackButtonClicked) {
      setShowModal(true);
    }
  };

  const toggle = () => {
    setShowModal(false);
    window.history.pushState(window.location.search, '');
    setBackbuttonPress(false);
  };

  const OK = () => {
    setShowModal(false);
    setBackbuttonPress(true);
    if (isOrganisationRegistration) {
      window.history.go(-2);
    }
    else {
      window.history.back();
    }
  };

  const exitModalParameters = {
    isOpen: showModal,
    toggleFunction: toggle,
    OKFunction: OK
  };

  // don't load UI until we have the data
  if ((!referenceData || !referenceData.value) ||
    (!rateCards || !rateCards.value) ||
    (!individualDrones || !individualDrones.value) ||
    (!organisationDrones || !organisationDrones.value)) {
    return <Loader message='Working. Please wait.....' />;
  }

  const buildDroneFormData = (): Array<DroneData> => {
    const droneData: Array<DroneData> = [];
    const crmDroneData = !isOrganisationRegistration ? individualDrones.value : organisationDrones.value;
    const expiringRPA = moment().startOf('day').add(daysRPAexpiring, 'days').subtract(1, 'days');
    const expiredRPA = moment().endOf('day').subtract(daysRPAexpired, 'days');
    let id = 1;
    crmDroneData.value.forEach((crmDrone) => {
      if (moment(crmDrone['casa_validuntil'], 'YYYY-MM-DD') > expiredRPA && moment(crmDrone['casa_validuntil'], 'YYYY-MM-DD') <= expiringRPA) {
        const droneRegistrationId = crmDrone['casa_droneregistrationid'];
        if (!droneRegId || droneRegId === droneRegistrationId) {
          const manufacturer = crmDrone['drone.casa_manufacturer'] ? crmDrone['drone.casa_manufacturer'] : crmDrone['manufacturer.casa_name'];
          let model = crmDrone['drone.casa_model'] ? crmDrone['drone.casa_model'] : crmDrone['model.casa_name'];
          if (manufacturer === 'Home built') {
            model = crmDrone['category.casa_name'];
          }
          const manufacturerCode = crmDrone['manufacturer.casa_code'];
          const serialNumberUpdateRequired = featureDroneSerialNumberValidationIsActive && manufacturerCode === DRONE_CONSTANTS.DJI_MANUFACTURER_CODE && crmDrone['drone.casa_serialnumbervalidationstatus'] === 852460002;
          let validUntilMessage = '';
          let validUntilMessageLevel = '';
          let renewedValidUntil = '';
          if (moment(crmDrone['casa_validuntil'], 'YYYY-MM-DD') < moment() && moment(crmDrone['casa_validuntil'], 'YYYY-MM-DD') > expiredRPA) {
            // expired
            validUntilMessage = 'Expired';
            validUntilMessageLevel = 'error';
            renewedValidUntil = moment().add(1, 'year').format('DD MMM YYYY');
          }
          if (moment(crmDrone['casa_validuntil'], 'YYYY-MM-DD') >= moment() && moment(crmDrone['casa_validuntil'], 'YYYY-MM-DD') <= expiringRPA) {
            // expiring
            validUntilMessage = 'Expiring soon';
            validUntilMessageLevel = 'warning';
            renewedValidUntil = moment(crmDrone['casa_validuntil'], 'YYYY-MM-DD').add(1, 'year').format('DD MMM YYYY');
          }
          const intendedUse = crmDrone['reftype.casa_name'].toLocaleLowerCase();
          droneData.push({
            id: id++,
            droneRegistrationId: droneRegistrationId,
            droneId: crmDrone['drone.casa_droneid'],
            manufacturerCode: manufacturerCode,
            manufacturer: manufacturer,
            modelCode: crmDrone['model.casa_code'] ?? '',
            model: model,
            modelUpdateRequired: false,
            categoryCode: crmDrone['category.casa_code'] ?? '',
            weightClass: crmDrone['droneWeightClass'] ?? crmDrone['droneWeightClassNoModelId'] ?? '',
            weightClassCode: crmDrone['droneWeightClassCode'] ?? crmDrone['droneWeightClassCodeNoModelId'] ?? '',
            serialNumber: crmDrone['drone.casa_serialnumber'],
            serialNumberUpdateRequired: serialNumberUpdateRequired,
            registeredOverseas: crmDrone['drone.casa_isregisteredoverseas'] === 1 ? 'yes' : 'no',
            validUntil: moment(crmDrone['casa_validuntil'], 'YYYY-MM-DD').format('DD MMM YYYY'),
            validUntilMessage: validUntilMessage,
            validUntilMessageLevel: validUntilMessageLevel,
            renewedValidUntil: renewedValidUntil,
            newExpiryDate: moment(renewedValidUntil, 'DD MMM YYYY').format(),
            intendedUse: intendedUse,
            fee: !serialNumberUpdateRequired ? determineFee(rateCards, crmDrone['droneWeightClassCode'], crmDrone['drone.casa_isregisteredoverseas'] === 1, intendedUse === 'commercial') : 0,
            serialNumberValidationStatus: featureDroneSerialNumberValidationIsActive && manufacturerCode === DRONE_CONSTANTS.DJI_MANUFACTURER_CODE && crmDrone['drone.casa_serialnumbervalidationstatus'] ? crmDrone['drone.casa_serialnumbervalidationstatus'].toString() : null
          });
        }
      }
    });
    return droneData;
  };

  const initialValues: DroneRegistrationFormData = {
    drones: buildDroneFormData(),
    declaration: false,
    totalAmount: 0
  };

  // Steps and step validation associated with the form
  const STEPS: FormStep[] = [
    {
      id: 'DroneRenewalSummary',
      render: () => (
        <DroneRenewalSummaryStep />
      ),
      validationSchema: yup.object().shape({
        drones: droneValidationSchema(referenceData, isOrganisationRegistration, individualDrones, organisationDrones, checkSerialNumberPrefix)
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD),
        declaration: yup.boolean()
          .required(FORM_ERROR_MESSAGES.DECLARATION)
          .oneOf([true], FORM_ERROR_MESSAGES.DECLARATION)
      })
    },
    {
      id: 'DroneDetails',
      render: (props: { droneIndex: number; }) => (
        <DroneDetailsStep {...props} />
      ),
      skipNavigation: true
    },
    {
      id: 'DroneEdit',
      render: (props?: DroneEditStepProps) => (
        <DroneEditStep {...props} applicationName='Update registration details' parent='renewal' />
      ),
      validationSchema: yup.object().shape({
        drones: droneValidationSchema(referenceData, isOrganisationRegistration, individualDrones, organisationDrones, checkSerialNumberPrefix)
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
      }),
      skipNavigation: true
    },
    {
      id: 'Final',
      render: (props?: FinalStepProps) => (
        <FinalStep {...props} isRenewal={true} />
      ),
      skipNavigation: true
    }
  ];

  const handleSubmit = async (values: DroneRegistrationFormData, actions: FormikHelpers<DroneRegistrationFormData & FormikValues>): Promise<IFormAlertType | boolean | null> => {
    setSubmissionInProgress(true);
    const { setSubmitting } = actions;

    // set some values required for submission
    values.drones.forEach(drone => {
      drone.intendedUse === 'recreational' ? drone.formCode = 'DRONE_R_RENEWAL_REC' : drone.formCode = 'DRONE_R_RENEWAL_COM';
      if (!checkIfOtherManufacturer(drone.manufacturerCode)) {
        drone.manufacturer = referenceData?.value.DRONE_MANUFACTURER.find(item => item.casa_code === drone.manufacturerCode)?.casa_name;
      }
      if (!checkIfOtherManufacturer(drone.manufacturerCode) && !checkIfOtherModel(drone.modelCode) && !checkIfHomeBuilt(drone.manufacturerCode)) {
        drone.model = referenceData?.value.DRONE_MODEL.find(item => item.casa_code === drone.modelCode)?.casa_name;
      }
      drone.category = referenceData?.value.DRONE_CATEGORY.find(item => item.casa_code === drone.categoryCode)?.casa_name;
      drone.weightClass = referenceData?.value.DRONE_WEIGHT_CLASS.find(item => item.casa_code === drone.weightClassCode)?.casa_name;
    });
    values.totalAmount = values.drones.reduce((accumulator, current) => accumulator + (current.fee || 0), 0);

    // Send request to Submission service
    try {
      const dronesRequestLimit = 100;
      const requestPayLoad = {
        orgId: organisationDetailsResponse?.value?.value[0]?.accountid,
        organisationArn: organisationDetailsResponse?.value?.value[0]?.casa_arn,
        formData: values.drones.length <= dronesRequestLimit ? values : {}
      };
      const formData = new FormData();
      formData.append('request', JSON.stringify(requestPayLoad));
      // send values as a json file if over 100 drones
      // this is to avoid request payload tripping WAF limit of 128kb
      if (values.drones.length > dronesRequestLimit) {
        const formDataBlob = new Blob([JSON.stringify(values)], { type: 'text/plain' });
        const formDataFile = new File([formDataBlob], 'formData.json');
        formData.append('files', formDataFile);
      }
      const response = await droneService.submitDroneRenewal(formData);

      // If request failed return error to the user
      if (!response || !response.wasSuccessful) {
        setSubmitting(false);
        setSubmissionInProgress(false);
        return {
          as: 'error',
          title: 'Unable to complete application online',
          message: 'Unfortunately this application cannot be completed online. Please contact us for further information.'
        };
      }
      else {
        // If successful but requires payment redirect to the payment gateway
        if (response.paymentToken) {
          // Redirect to payment screen
          window.location.href = B2CSettings.items.WestpacQuickWebURL + response.paymentToken;
          await new Promise(resolve => setTimeout(resolve, 2000));
          return null;
        }
        else {
          setSubmitting(false);
          setSubmissionInProgress(false);
          // If successful but no payment required then return success to the user
          return {
            as: 'success',
            title: 'Renewal successful',
            message: ''
          };
        }
      }
    }
    catch (e: any) {
      setSubmitting(false);
      setSubmissionInProgress(false);
      return {
        as: 'error',
        title: 'Unable to complete application online',
        message: 'Unfortunately this application cannot be completed online. Please contact us for further information.'
      };
    }
  };

  const getDraftRecordId = () => {
    // process url parameters that require base64 decoding
    const params = processUrlParameters(window.location.search);
    if (params && params.recordid) {
      return params.recordid;
    }
    // check for cancelled or failed payment and load draft if true
    const returnParameters = queryString.parse(window.location.search);
    if (returnParameters.summaryCode !== '0' && returnParameters.customRef) {
      return returnParameters.customRef;
    }
    return '';
  };

  // TODO: this needs to be looked at, breadcrumbs shouldn't be hard coded.
  // Options are use breadcrumbs passed in params or eventually have an SPA
  // wide breadcrumb solution
  const breadcrumbs: IAuBreadcrumbItem[] = [
    { link: dashboardUrl, text: 'myCASA' },
    { link: manageDronesUrl, text: 'Manage registered drones' },
    { text: <>Renew <abbr title="Remotely piloted aircraft">drone</abbr> registration</> }
  ];

  const getDraftSuccessReturnUrl = () => {
    return `${manageDronesUrl}&saveSuccess=1`;
  };

  return (
    <>
      <AuBreadcrumbs label='breadcrumbs' items={breadcrumbs} />
      <FormContextProvider
        initialValues={initialValues}
        handleSubmit={handleSubmit}
        steps={STEPS}
        draftRecordId={getDraftRecordId()}
        draftSuccessReturnUrl={getDraftSuccessReturnUrl()}
        draftFailReturnUrl={manageDronesUrl}>
        <FormContext.Consumer>
          {({ currentStepIndex, steps, currentStepProps }: IFormContextType) => (
            <Form name='Renew drone registration'>
              <FormAlert htmlFor='Renew drone registration' />
              <ExitModal exitModalParams={exitModalParameters} />
              {steps[currentStepIndex].render(currentStepProps)}
            </Form>
          )}
        </FormContext.Consumer>
      </FormContextProvider>
    </>
  );
};

export default DroneRenewal;